<template>
  <div class="affiliate-footer-bar">
    <div class="affiliate-footer-bar__container">
      <div class="affiliate-footer-bar__left-container">
        <PxButton
          v-if="shouldShowElements"
          v-bind="saveAsDraftButtonProps"
          :loading="isSaving"
          class="affiliate-footer-bar__draft-button"
          @click="saveDraft"
        />

        <ElCheckbox
          v-if="shouldShowConsentButton"
          v-model="sdgConsent"
          class="checkbox"
        >
          By submitting this form, I agree to share data provided in accordance
          with Abaca's
          <a href="https://abaca.app/terms-of-use">Terms of Use</a>
          and
          <a href="https://abaca.app/terms-of-use">Privacy Policy</a>
        </ElCheckbox>

        <p
          v-if="date && shouldShowElements"
          class="affiliate-footer-bar__notification"
        >
          {{
            $t("affiliateProgram.footerBar.update", {
              date,
            })
          }}
        </p>
      </div>
      <div class="affiliate-footer-bar__right-container">
        <PxButton
          v-bind="previousButtonProps"
          :loading="isSaving"
          class="affiliate-footer-bar__previous-button"
          @click="goToPreviousStep"
        />
        <PxButton
          v-bind="nextButtonProps"
          :disabled="buttonIsDisabled"
          :loading="isSaving"
          class="affiliate-footer-bar__next-button"
          @click="goToNextStep"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import {
  EPxButtonSize,
  EPxButtonVariant,
  EPxButtonType,
} from "@/components/px-button/px-button.types";
import {
  EAffiliatesActions,
  EAffiliatesMutations,
} from "@/modules/affiliates/services/store/affiliates/affiliates.types";
import { IAffiliatesState } from "@/modules/affiliates/services/store/affiliates/affiliates.types";

export default defineComponent({
  name: "AffiliateFooterBar",

  props: {
    isLastPage: {
      type: Boolean,
      default: false,
    },

    date: {
      type: String,
      default: "",
    },

    isSaving: {
      type: Boolean,
      default: false,
    },

    submitDisabled: {
      type: Boolean,
      default: false,
    },

    questionBundleAffiliate: {
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      sdgConsent: false,
    };
  },
  static: {
    saveAsDraftButton: {
      size: EPxButtonSize.MEDIUM,
      variant: EPxButtonVariant.FLUID,
    },

    previousButton: {
      type: EPxButtonType.LINK,
    },

    nextButton: {
      size: EPxButtonSize.MEDIUM,
    },
  },

  computed: {
    buttonIsDisabled(): boolean {
      return (
        (this.submitDisabled &&
          this.questionBundleAffiliate &&
          this.isLastPage) ||
        (this.isSDGFlow && !this.sdgConsent && this.isLastPage)
      );
    },

    affiliatesData(): IAffiliatesState {
      return this.$store.state.affiliates;
    },

    isSDGFlow(): boolean {
      return this.affiliatesData?.affiliate?.flow_type === 2;
    },

    saveAsDraftButtonProps(): any {
      return {
        ...this.$options.static.saveAsDraftButton,
        label: this.$t("common.saveAsDraft"),
      };
    },

    previousButtonProps(): any {
      return {
        ...this.$options.static.previousButton,
        label: this.$t("common.previous"),
      };
    },

    nextButtonProps(): any {
      return {
        ...this.$options.static.nextButton,
        type:
          this.isLastPage && this.questionBundleAffiliate
            ? EPxButtonType.GREEN
            : EPxButtonType.BLUE,
        label: this.isLastPage
          ? this.$t("common.submit")
          : this.$t("common.next"),
      };
    },

    shouldShowElements(): boolean {
      return (
        ((this.questionBundleAffiliate && !this.isLastPage) ||
          !this.questionBundleAffiliate) &&
        !this.isSDGFlow
      );
    },
    shouldShowConsentButton(): boolean {
      return this.questionBundleAffiliate && this.isLastPage && this.isSDGFlow;
    },
  },

  methods: {
    async saveDraft() {
      this.$store.commit(`affiliates/${EAffiliatesMutations.SET_SAVING}`, true);
      await this.$store.dispatch(EAffiliatesActions.DEBOUNCE_SAVE_DRAFT);
      this.$emit("show-saved-draft-message");
    },

    goToPreviousStep() {
      this.$emit("previous-step");
    },

    goToNextStep() {
      this.$emit("next-step");
    },
  },
});
</script>

<style lang="scss" scoped>
.affiliate-footer-bar {
  min-height: 80px;
  height: auto;
  width: 100%;

  // Safe area padding
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);

  z-index: z("navigation") + 1;
  background: $white;
  position: relative;
  box-shadow:
    0 -1px 0 0 #dcdde24c,
    0 -6px 6px 0 #00000006;

  &__container {
    @include make-container-max-widths();

    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1140px;
    min-height: 80px;
    margin: 0 auto;

    @include breakpoint-down(md) {
      padding: 0 20px;
    }
  }

  &__left-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__draft-button {
    padding: 0 15px;
  }

  &__notification {
    @include grotesk(regular, italic);

    margin: 0;
    color: $manatee;
    font-size: 13px;
    line-height: 1.38;

    @include breakpoint-down(md) {
      position: absolute;
      bottom: calc(2px + constant(safe-area-inset-bottom));
      bottom: calc(2px + env(safe-area-inset-bottom));
      left: 0;
      width: 100%;
      font-size: 10px;
      text-align: center;
    }
  }

  &__right-container {
    display: flex;
    gap: 14px;
  }

  &__next-button {
    min-width: 66px;
  }

  &__previous-button :deep() span {
    font-size: 14px;
  }

  &__sdg_consent {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
  }
}
</style>
